/**
 * Trovva — Agent Activity Dashboard (Redesigned)
 *
 * Broker's morning dashboard: one table, all agents, instant clarity.
 *
 * Rows  = agents (5–20 for indie NJ brokerages)
 * Cols  = Overdue · Due Today · On Track · Score · Active Leads ·
 *         Speed to Lead · Follow-ups (7d) · Pipeline $ · Last Activity · Trend · Actions
 *
 * Drill-down: click an agent row to expand inline detail panel
 * Smart Ping: multi-channel (Email + SMS + WhatsApp) with dedup notes
 */
import React, { useState, useMemo } from 'react';
import {
  AlertTriangle, CheckCircle, Clock, ChevronDown, ChevronUp,
  TrendingUp, TrendingDown, Minus, MessageSquare, Mail, Phone,
  Send, Search, RefreshCw, Loader2, X, DollarSign, Zap,
  Activity, Users, ArrowRight, Edit3, ExternalLink, Bell,
  Calendar, UserCheck, BarChart3, Trophy, Award, Medal,
  LayoutList, BarChart2, Target,
} from 'lucide-react';
import { theme } from '../../lib/theme';
import EmptyState from '../common/EmptyState';
import LoadingSpinner from '../common/LoadingSpinner';
import { generateTasks, getTaskStatus } from '../../lib/taskTemplates';
import { TEAM_MEMBERS } from '../../lib/constants';
import {
  buildRoster, letterGrade as _letterGrade, gradeColor,
  SEGMENT_META,
} from '../../lib/agentScoring';

// ════════════════════════════════════════════════════════════════
// DEMO DATA
// ════════════════════════════════════════════════════════════════

const DEMO_ASSIGNMENTS = [
  { leadId: 'L1', leadName: 'Sarah Mitchell',   leadType: 'Buyer',      agentName: 'Sylvia Micarelli',  assignedDate: '2026-04-08' },
  { leadId: 'L3', leadName: 'Maria Rodriguez',  leadType: 'Seller',     agentName: 'Jenifer Raffa',     assignedDate: '2026-04-07' },
  { leadId: 'L4', leadName: 'James Wilson',      leadType: 'Buyer',      agentName: 'Sylvia Micarelli',  assignedDate: '2026-04-05' },
  { leadId: 'L2', leadName: 'David Chen',        leadType: 'Renter',     agentName: 'Neha',              assignedDate: '2026-04-09' },
  { leadId: 'L5', leadName: 'NJ Dental Group',   leadType: 'Commercial', agentName: 'Patricia Furrow',   assignedDate: '2026-04-06' },
  { leadId: 'L6', leadName: 'Tom Patel',         leadType: 'Buyer',      agentName: 'Desiree Hogbin',    assignedDate: '2026-04-10' },
  { leadId: 'L7', leadName: 'Lisa Park',         leadType: 'Seller',     agentName: 'Johanna Micarelli', assignedDate: '2026-04-04' },
  { leadId: 'L8', leadName: 'Raj Mehta',         leadType: 'Buyer',      agentName: 'Jenifer Raffa',     assignedDate: '2026-04-11' },
  { leadId: 'L9', leadName: 'Angela Brooks',     leadType: 'Renter',     agentName: 'Patricia Furrow',   assignedDate: '2026-04-03' },
  { leadId: 'L10', leadName: 'Mike Sullivan',    leadType: 'Commercial', agentName: 'Neha',              assignedDate: '2026-04-12' },
];

// Demo pipeline values per agent
const DEMO_PIPELINE = {
  'Sylvia Micarelli':  { value: 1_250_000, deals: 3, activeLeads: 8, speedToLead: 12 },
  'Jenifer Raffa':     { value: 875_000,   deals: 2, activeLeads: 6, speedToLead: 8 },
  'Johanna Micarelli': { value: 425_000,   deals: 1, activeLeads: 4, speedToLead: 45 },
  'Desiree Hogbin':    { value: 620_000,   deals: 2, activeLeads: 5, speedToLead: 22 },
  'Patricia Furrow':   { value: 1_800_000, deals: 4, activeLeads: 9, speedToLead: 6 },
  'Neha':              { value: 340_000,   deals: 1, activeLeads: 3, speedToLead: 35 },
};

function generateDemoTasks() {
  let allTasks = [];
  DEMO_ASSIGNMENTS.forEach(a => {
    const tasks = generateTasks(a.leadType, a.assignedDate, a.agentName, a.leadName, a.leadId);
    tasks.forEach((t, i) => {
      const status = getTaskStatus(t);
      if (status === 'overdue' && i < 3) { t.status = 'completed'; t.completedDate = t.dueDate; }
      else if (status === 'overdue') { t.status = 'overdue'; }
    });
    allTasks = [...allTasks, ...tasks];
  });
  return allTasks;
}

// ════════════════════════════════════════════════════════════════
// FORMAT HELPERS
// ════════════════════════════════════════════════════════════════

function formatCurrency(val) {
  if (!val) return '—';
  if (val >= 1_000_000) return `$${(val / 1_000_000).toFixed(1)}M`;
  if (val >= 1_000) return `$${(val / 1_000).toFixed(0)}K`;
  return `$${val}`;
}

function formatSpeed(minutes) {
  if (minutes == null) return '—';
  if (minutes < 60) return `${minutes}m`;
  return `${(minutes / 60).toFixed(1)}h`;
}

function timeAgo(days) {
  if (days === 0) return 'Today';
  if (days === 1) return '1d ago';
  if (days < 7) return `${days}d ago`;
  return `${Math.floor(days / 7)}w ago`;
}

function initials(name) {
  return (name || '').split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
}

// ════════════════════════════════════════════════════════════════
// MINI SPARKLINE (SVG)
// ════════════════════════════════════════════════════════════════

function Sparkline({ series, trend, width = 60, height = 20 }) {
  if (!series || series.length < 2) return null;
  const max = Math.max(...series, 1);
  const pts = series.map((v, i) => {
    const x = (i / (series.length - 1)) * width;
    const y = height - (v / max) * (height - 4) - 2;
    return `${x},${y}`;
  }).join(' ');
  const color = trend === 'up' ? theme.colors.success : trend === 'down' ? theme.colors.danger : theme.colors.textLight;
  return (
    <svg width={width} height={height} style={{ display: 'block' }}>
      <polyline fill="none" stroke={color} strokeWidth={1.5} points={pts} />
    </svg>
  );
}

// ════════════════════════════════════════════════════════════════
// SMART MULTI-CHANNEL PING MODAL
// ════════════════════════════════════════════════════════════════

function SmartPingModal({ agent, onClose, showToast }) {
  const [message, setMessage] = useState('');
  const [channels, setChannels] = useState({ email: true, sms: true, whatsapp: false });
  const [sending, setSending] = useState(false);

  const firstName = (agent?.name || '').split(' ')[0];
  const channelCount = Object.values(channels).filter(Boolean).length;

  const toggleChannel = (ch) => setChannels(prev => ({ ...prev, [ch]: !prev[ch] }));

  const handleSend = async () => {
    if (!message.trim()) return;
    setSending(true);

    // Build per-channel messages with dedup notes
    const activeChannels = Object.entries(channels).filter(([, v]) => v).map(([k]) => k);
    const otherChannelNames = (ch) => activeChannels.filter(c => c !== ch).map(c =>
      c === 'email' ? 'email' : c === 'sms' ? 'text' : 'WhatsApp'
    );

    const _dedupNote = (ch) => {
      const others = otherChannelNames(ch);
      if (others.length === 0) return '';
      if (others.length === 1) return `\n\n(Also sent via ${others[0]} — no need to respond twice.)`;
      return `\n\n(Also sent via ${others.join(' and ')} — just respond once, wherever you see this first.)`;
    };

    // In production, each channel calls its respective API
    // For now, simulate sending
    await new Promise(r => setTimeout(r, 800));
    setSending(false);
    const channelLabels = activeChannels.map(c => c === 'email' ? 'Email' : c === 'sms' ? 'SMS' : 'WhatsApp');
    showToast?.(`Ping sent to ${firstName} via ${channelLabels.join(', ')}`);
    onClose();
  };

  const channelOptions = [
    { key: 'email',    label: 'Email',    Icon: Mail,           color: '#3B82F6' },
    { key: 'sms',      label: 'SMS',      Icon: MessageSquare,  color: '#22C55E' },
    { key: 'whatsapp', label: 'WhatsApp', Icon: Phone,          color: '#25D366' },
  ];

  if (!agent) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200,
    }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{
        background: theme.colors.card, borderRadius: 16, width: '95%', maxWidth: 480,
        padding: 24, boxShadow: theme.shadow.lg,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
              Ping {agent.name}
            </h3>
            <p style={{ fontSize: 12, color: theme.colors.textLight, margin: '4px 0 0' }}>
              Send on multiple channels — agent responds once
            </p>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <X size={18} color={theme.colors.textLight} />
          </button>
        </div>

        {/* Channel toggles */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {channelOptions.map(({ key, label, color }) => {
            const active = channels[key];
            return (
              <button key={key} onClick={() => toggleChannel(key)} style={{
                flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                border: active ? `2px solid ${color}` : `1px solid ${theme.colors.border}`,
                background: active ? `${color}10` : theme.colors.bg,
                color: active ? color : theme.colors.textLight,
                fontSize: 12, fontWeight: 600, transition: 'all .15s',
              }}>
                <Icon size={14} />
                {label}
              </button>
            );
          })}
        </div>

        {channelCount > 1 && (
          <div style={{
            fontSize: 11, color: theme.colors.textLight, padding: '8px 12px', marginBottom: 12,
            background: `${theme.colors.info}08`, borderRadius: 8, lineHeight: 1.5,
          }}>
            Each channel will include a note: "Also sent via [other channels] — no need to respond twice."
          </div>
        )}

        {/* Message */}
        <textarea
          placeholder={`Message for ${firstName}...`}
          value={message}
          onChange={e => setMessage(e.target.value)}
          rows={4}
          style={{
            width: '100%', padding: 12, fontSize: 13, borderRadius: 10,
            border: `1px solid ${theme.colors.border}`, outline: 'none',
            background: theme.colors.bg, resize: 'vertical', fontFamily: 'inherit',
            boxSizing: 'border-box',
          }}
        />

        {/* Quick messages */}
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {[
            `Hi ${firstName}, checking in — how's your pipeline looking?`,
            `${firstName}, you have overdue follow-ups. Need help prioritizing?`,
            `Quick reminder to update your lead statuses when you get a chance.`,
          ].map((q, i) => (
            <button key={i} onClick={() => setMessage(q)} style={{
              fontSize: 11, padding: '5px 10px', borderRadius: 6,
              border: `1px solid ${theme.colors.border}`, background: theme.colors.bg,
              color: theme.colors.text, cursor: 'pointer', whiteSpace: 'nowrap',
            }}>
              {q.length > 50 ? q.slice(0, 48) + '…' : q}
            </button>
          ))}
        </div>

        {/* Send */}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button onClick={onClose} style={{
            padding: '9px 18px', fontSize: 13, borderRadius: 8,
            border: `1px solid ${theme.colors.border}`, background: 'white',
            cursor: 'pointer', color: theme.colors.text,
          }}>Cancel</button>
          <button onClick={handleSend} disabled={sending || !message.trim() || channelCount === 0} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '9px 18px', fontSize: 13, fontWeight: 600, borderRadius: 8,
            border: 'none', cursor: sending ? 'wait' : 'pointer',
            background: channelCount === 0 ? theme.colors.border : theme.colors.primary,
            color: 'white', opacity: (!message.trim() || channelCount === 0) ? 0.5 : 1,
          }}>
            {sending ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Send size={14} />}
            Send{channelCount > 1 ? ` on ${channelCount} channels` : ''}
          </button>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// EXPANDED AGENT DETAIL PANEL
// ════════════════════════════════════════════════════════════════

function AgentDetail({ agent, tasks, pipeline, onPing }) {
  const [tab, setTab] = useState('overview');
  const today = new Date().toISOString().split('T')[0];
  const overdueTasks = (tasks || []).filter(t => t.status !== 'completed' && t.dueDate < today);
  const dueTodayTasks = (tasks || []).filter(t => t.status !== 'completed' && t.dueDate === today);
  const recentCompleted = (tasks || []).filter(t => t.status === 'completed').slice(-5).reverse();

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'overdue',  label: `Overdue (${overdueTasks.length})` },
    { id: 'recent',   label: 'Recent Activity' },
    { id: 'pipeline', label: 'Pipeline' },
  ];

  return (
    <div style={{
      padding: '16px 20px', background: `${theme.colors.primary}04`,
      borderTop: `1px solid ${theme.colors.border}`,
    }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 14 }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '5px 12px', fontSize: 11, fontWeight: 600, borderRadius: 6,
            border: 'none', cursor: 'pointer',
            background: tab === t.id ? theme.colors.primary : 'transparent',
            color: tab === t.id ? 'white' : theme.colors.textLight,
          }}>{t.label}</button>
        ))}
      </div>

      {/* Overview */}
      {tab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {[
            { label: 'Total Tasks',    value: agent.metrics.total,     color: theme.colors.text },
            { label: 'Completed',       value: agent.metrics.completed, color: theme.colors.success },
            { label: 'Overdue',         value: agent.metrics.overdue,   color: agent.metrics.overdue > 0 ? theme.colors.danger : theme.colors.success },
            { label: 'Completion Rate', value: agent.scoreBreakdown?.completion != null ? `${agent.scoreBreakdown.completion}%` : '—', color: theme.colors.primary },
            { label: 'On-Time Rate',    value: agent.scoreBreakdown?.onTime != null ? `${agent.scoreBreakdown.onTime}%` : '—', color: theme.colors.info },
            { label: 'Days Inactive',   value: agent.metrics.daysSinceActivity, color: agent.metrics.daysSinceActivity >= 3 ? theme.colors.danger : theme.colors.success },
          ].map((m, i) => (
            <div key={i} style={{
              padding: 12, borderRadius: 8, background: theme.colors.card,
              border: `1px solid ${theme.colors.border}`,
            }}>
              <div style={{ fontSize: 20, fontWeight: 700, color: m.color }}>{m.value}</div>
              <div style={{ fontSize: 10, color: theme.colors.textLight, marginTop: 2 }}>{m.label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Overdue tasks */}
      {tab === 'overdue' && (
        <div>
          {overdueTasks.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.colors.success, padding: 10 }}>No overdue tasks!</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {overdueTasks.slice(0, 10).map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderRadius: 6, background: theme.colors.card,
                  border: `1px solid ${theme.colors.danger}20`, fontSize: 12,
                }}>
                  <AlertTriangle size={12} color={theme.colors.danger} />
                  <span style={{ flex: 1, color: theme.colors.text }}>{t.title}</span>
                  <span style={{ fontSize: 10, color: theme.colors.textLight }}>{t.leadName}</span>
                  <span style={{ fontSize: 10, color: theme.colors.danger, fontWeight: 600 }}>Due {t.dueDate}</span>
                </div>
              ))}
              {overdueTasks.length > 10 && (
                <div style={{ fontSize: 11, color: theme.colors.textLight, padding: '4px 12px' }}>
                  +{overdueTasks.length - 10} more
                </div>
              )}
            </div>
          )}
          {dueTodayTasks.length > 0 && (
            <>
              <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.warning, margin: '12px 0 6px', display: 'flex', alignItems: 'center', gap: 4 }}>
                <Clock size={12} /> Due Today ({dueTodayTasks.length})
              </div>
              {dueTodayTasks.slice(0, 5).map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderRadius: 6, background: theme.colors.card,
                  border: `1px solid ${theme.colors.warning}20`, fontSize: 12, marginBottom: 4,
                }}>
                  <Clock size={12} color={theme.colors.warning} />
                  <span style={{ flex: 1, color: theme.colors.text }}>{t.title}</span>
                  <span style={{ fontSize: 10, color: theme.colors.textLight }}>{t.leadName}</span>
                </div>
              ))}
            </>
          )}
        </div>
      )}

      {/* Recent activity */}
      {tab === 'recent' && (
        <div>
          {recentCompleted.length === 0 ? (
            <div style={{ fontSize: 12, color: theme.colors.textLight, padding: 10 }}>No recent completions.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {recentCompleted.map(t => (
                <div key={t.id} style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px',
                  borderRadius: 6, background: theme.colors.card,
                  border: `1px solid ${theme.colors.border}`, fontSize: 12,
                }}>
                  <CheckCircle size={12} color={theme.colors.success} />
                  <span style={{ flex: 1, color: theme.colors.text }}>{t.title}</span>
                  <span style={{ fontSize: 10, color: theme.colors.textLight }}>{t.leadName}</span>
                  <span style={{ fontSize: 10, color: theme.colors.success }}>{t.completedDate || ''}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Pipeline */}
      {tab === 'pipeline' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
          {[
            { label: 'Pipeline Value', value: formatCurrency(pipeline?.value), Icon: DollarSign, color: theme.colors.success },
            { label: 'Active Deals',   value: pipeline?.deals || 0,            Icon: BarChart3,   color: theme.colors.primary },
            { label: 'Active Leads',   value: pipeline?.activeLeads || 0,      Icon: Users,       color: theme.colors.info },
            { label: 'Speed to Lead',  value: formatSpeed(pipeline?.speedToLead), Icon: Zap,      color: pipeline?.speedToLead <= 15 ? theme.colors.success : pipeline?.speedToLead <= 30 ? theme.colors.warning : theme.colors.danger },
          ].map((m, i) => (
            <div key={i} style={{
              padding: 12, borderRadius: 8, background: theme.colors.card,
              border: `1px solid ${theme.colors.border}`, display: 'flex', alignItems: 'center', gap: 10,
            }}>
              <div style={{
                width: 32, height: 32, borderRadius: 8, background: `${m.color}12`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <m.Icon size={16} color={m.color} />
              </div>
              <div>
                <div style={{ fontSize: 16, fontWeight: 700, color: m.color }}>{m.value}</div>
                <div style={{ fontSize: 10, color: theme.colors.textLight }}>{m.label}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Ping from detail */}
      <div style={{ marginTop: 14, display: 'flex', gap: 8 }}>
        <button onClick={() => onPing(agent)} style={{
          display: 'flex', alignItems: 'center', gap: 6,
          padding: '8px 16px', borderRadius: 8, fontSize: 12, fontWeight: 600,
          border: 'none', background: theme.colors.primary, color: 'white', cursor: 'pointer',
        }}>
          <Send size={13} /> Ping {(agent.name || '').split(' ')[0]}
        </button>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// AGENT TABLE ROW
// ════════════════════════════════════════════════════════════════

function AgentRow({ agent, tasks, pipeline, expanded, onToggle, onPing, hidePing }) {
  const today = new Date().toISOString().split('T')[0];
  const agentTasks = tasks || [];
  const overdue = agentTasks.filter(t => t.status !== 'completed' && t.dueDate < today).length;
  const dueToday = agentTasks.filter(t => t.status !== 'completed' && t.dueDate === today).length;
  const onTrack = agentTasks.filter(t => t.status !== 'completed' && t.dueDate >= today).length;
  const last7 = agentTasks.filter(t => {
    if (t.status !== 'completed' || !t.completedDate) return false;
    const d = new Date(t.completedDate);
    const weekAgo = new Date(); weekAgo.setDate(weekAgo.getDate() - 7);
    return d >= weekAgo;
  }).length;

  const gc = gradeColor(agent.grade, theme.colors);
  const segMeta = SEGMENT_META[agent.segment] || SEGMENT_META.on_track;

  const cellStyle = { padding: '12px 8px', fontSize: 13, textAlign: 'center', verticalAlign: 'middle' };
  const numCell = (val, color, danger) => (
    <td style={{ ...cellStyle, fontWeight: 700, color: danger && val > 0 ? color : val > 0 ? color : theme.colors.textLight }}>
      {val}
    </td>
  );

  return (
    <>
      <tr
        onClick={onToggle}
        style={{
          cursor: 'pointer', transition: 'background .1s',
          background: expanded ? `${theme.colors.primary}06` : 'transparent',
          borderBottom: expanded ? 'none' : undefined,
        }}
        onMouseEnter={e => { if (!expanded) e.currentTarget.style.background = `${theme.colors.primary}04`; }}
        onMouseLeave={e => { if (!expanded) e.currentTarget.style.background = 'transparent'; }}
      >
        {/* Agent */}
        <td style={{ ...cellStyle, textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10, padding: '12px 12px' }}>
          <div style={{
            width: 34, height: 34, borderRadius: 10, flexShrink: 0,
            background: `${segMeta.color}18`, color: segMeta.color,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 700,
          }}>
            {initials(agent.name)}
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>{agent.name}</div>
            <div style={{ fontSize: 10, color: segMeta.color, fontWeight: 600 }}>{segMeta.label}</div>
          </div>
        </td>

        {/* Score */}
        <td style={cellStyle}>
          <span style={{
            display: 'inline-block', padding: '3px 10px', borderRadius: 6,
            fontSize: 12, fontWeight: 700, background: `${gc}15`, color: gc,
            minWidth: 36, textAlign: 'center',
          }}>
            {agent.grade} {agent.score != null ? `(${agent.score})` : ''}
          </span>
        </td>

        {/* Overdue */}
        {numCell(overdue, theme.colors.danger, true)}

        {/* Due Today */}
        {numCell(dueToday, theme.colors.warning, true)}

        {/* On Track */}
        {numCell(onTrack, theme.colors.success, false)}

        {/* Active Leads */}
        <td style={{ ...cellStyle, fontWeight: 600, color: theme.colors.text }}>
          {pipeline?.activeLeads || '—'}
        </td>

        {/* Speed to Lead */}
        <td style={cellStyle}>
          <span style={{
            fontWeight: 600,
            color: pipeline?.speedToLead == null ? theme.colors.textLight
              : pipeline.speedToLead <= 15 ? theme.colors.success
              : pipeline.speedToLead <= 30 ? theme.colors.warning
              : theme.colors.danger,
          }}>
            {formatSpeed(pipeline?.speedToLead)}
          </span>
        </td>

        {/* Follow-ups 7d */}
        <td style={{ ...cellStyle, fontWeight: 600, color: last7 > 0 ? theme.colors.primary : theme.colors.textLight }}>
          {last7}
        </td>

        {/* Pipeline $ */}
        <td style={{ ...cellStyle, fontWeight: 600, color: theme.colors.success }}>
          {formatCurrency(pipeline?.value)}
        </td>

        {/* Last Activity */}
        <td style={{
          ...cellStyle, fontSize: 11,
          color: agent.metrics.daysSinceActivity >= 3 ? theme.colors.danger
            : agent.metrics.daysSinceActivity >= 1 ? theme.colors.warning
            : theme.colors.success,
          fontWeight: 600,
        }}>
          {timeAgo(agent.metrics.daysSinceActivity)}
        </td>

        {/* Trend */}
        <td style={cellStyle}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
            <Sparkline series={agent.trend?.series} trend={agent.trend?.trend} width={48} height={18} />
            {agent.trend?.trend === 'up' && <TrendingUp size={12} color={theme.colors.success} />}
            {agent.trend?.trend === 'down' && <TrendingDown size={12} color={theme.colors.danger} />}
            {agent.trend?.trend === 'flat' && <Minus size={10} color={theme.colors.textLight} />}
          </div>
        </td>

        {/* Actions */}
        {!hidePing && (
          <td style={{ ...cellStyle, display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center' }}>
            <button
              onClick={e => { e.stopPropagation(); onPing(agent); }}
              title="Ping agent"
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                width: 30, height: 30, borderRadius: 8,
                border: `1px solid ${theme.colors.primary}30`, background: 'transparent',
                cursor: 'pointer', color: theme.colors.primary,
              }}
            >
              <Send size={13} />
            </button>
            <div style={{ color: theme.colors.textLight, display: 'flex', alignItems: 'center' }}>
              {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </div>
          </td>
        )}
      </tr>

      {/* Expanded detail panel */}
      {expanded && (
        <tr>
          <td colSpan={12} style={{ padding: 0, borderBottom: `2px solid ${theme.colors.primary}20` }}>
            <AgentDetail agent={agent} tasks={agentTasks} pipeline={pipeline} onPing={onPing} />
          </td>
        </tr>
      )}
    </>
  );
}

// ════════════════════════════════════════════════════════════════
// LEADERBOARD VIEW (positive metrics only — safe for agents to see)
// ════════════════════════════════════════════════════════════════

const RANK_ICONS = [Trophy, Award, Medal]; // 🥇🥈🥉
const RANK_COLORS = ['#F59E0B', '#94A3B8', '#CD7F32'];

function LeaderboardView({ roster, tasksByAgent, currentAgentName }) {
  // Rank by score descending — only agents with scores
  const ranked = useMemo(() => {
    return [...roster]
      .filter(a => a.score != null)
      .sort((a, b) => (b.score || 0) - (a.score || 0))
      .map((a, i) => ({ ...a, rank: i + 1 }));
  }, [roster]);

  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);

  return (
    <div style={{
      background: theme.colors.card, borderRadius: 12,
      border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 18px', borderBottom: `1px solid ${theme.colors.border}`,
        display: 'flex', alignItems: 'center', gap: 8,
      }}>
        <Trophy size={16} color="#F59E0B" />
        <span style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text }}>Team Leaderboard</span>
        <span style={{ fontSize: 11, color: theme.colors.textLight, marginLeft: 'auto' }}>Ranked by performance score</span>
      </div>

      {ranked.length === 0 ? (
        <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight, fontSize: 13 }}>
          No scores available yet.
        </div>
      ) : (
        <div>
          {ranked.map(agent => {
            const agentTasks = tasksByAgent[agent.name] || [];
            const completions7d = agentTasks.filter(t => {
              if (t.status !== 'completed' || !t.completedDate) return false;
              return new Date(t.completedDate) >= weekAgo;
            }).length;
            const pipeline = DEMO_PIPELINE[agent.name];
            const gc = gradeColor(agent.grade, theme.colors);
            const isMe = currentAgentName && agent.name.toLowerCase() === currentAgentName.toLowerCase();
            const RankIcon = RANK_ICONS[agent.rank - 1];

            return (
              <div key={agent.name} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: '14px 18px',
                borderBottom: `1px solid ${theme.colors.border}50`,
                background: isMe ? `${theme.colors.primary}08` : 'transparent',
              }}>
                {/* Rank */}
                <div style={{
                  width: 32, height: 32, borderRadius: 10, flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: agent.rank <= 3 ? `${RANK_COLORS[agent.rank - 1]}18` : `${theme.colors.border}50`,
                  fontSize: 14, fontWeight: 800,
                  color: agent.rank <= 3 ? RANK_COLORS[agent.rank - 1] : theme.colors.textLight,
                }}>
                  {agent.rank <= 3 && RankIcon ? <RankIcon size={16} /> : `#${agent.rank}`}
                </div>

                {/* Avatar + Name */}
                <div style={{
                  width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                  background: `${gc}18`, color: gc,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 12, fontWeight: 700,
                }}>
                  {initials(agent.name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: theme.colors.text }}>
                    {agent.name}
                    {isMe && <span style={{ fontSize: 10, fontWeight: 700, color: theme.colors.primary, marginLeft: 6 }}>YOU</span>}
                  </div>
                </div>

                {/* Score badge */}
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 50,
                }}>
                  <span style={{
                    padding: '3px 10px', borderRadius: 6,
                    fontSize: 13, fontWeight: 700, background: `${gc}15`, color: gc,
                  }}>
                    {agent.grade}
                  </span>
                  <span style={{ fontSize: 10, color: theme.colors.textLight }}>{agent.score}</span>
                </div>

                {/* Positive metrics — no overdue, no negatives */}
                <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
                  <div style={{ textAlign: 'center', minWidth: 55 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.primary }}>{completions7d}</div>
                    <div style={{ fontSize: 9, color: theme.colors.textLight }}>Done (7d)</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 55 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.success }}>
                      {formatSpeed(pipeline?.speedToLead)}
                    </div>
                    <div style={{ fontSize: 9, color: theme.colors.textLight }}>Response</div>
                  </div>
                  <div style={{ textAlign: 'center', minWidth: 55 }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.success }}>
                      {formatCurrency(pipeline?.value)}
                    </div>
                    <div style={{ fontSize: 9, color: theme.colors.textLight }}>Pipeline</div>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 40 }}>
                    {agent.trend?.trend === 'up' && <TrendingUp size={14} color={theme.colors.success} />}
                    {agent.trend?.trend === 'down' && <TrendingDown size={14} color={theme.colors.danger} />}
                    {agent.trend?.trend === 'flat' && <Minus size={12} color={theme.colors.textLight} />}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN: AGENT ACTIVITY DASHBOARD
// ════════════════════════════════════════════════════════════════

const AgentActivityView = ({ showToast, userRole, userName }) => {
  const [tasks] = useState(generateDemoTasks);
  const [expandedAgent, setExpandedAgent] = useState(null);
  const [pingAgent, setPingAgent] = useState(null);
  const [sortCol, setSortCol] = useState('score');
  const [sortDir, setSortDir] = useState('desc');
  const [searchQ, setSearchQ] = useState('');
  const [viewMode, setViewMode] = useState('table'); // 'table' | 'leaderboard'

  const isBroker = !userRole || userRole === 'broker' || userRole === 'admin';

  // Build roster
  const tasksByAgent = useMemo(() => {
    const groups = {};
    tasks.forEach(t => { if (t.agentName) (groups[t.agentName] = groups[t.agentName] || []).push(t); });
    return groups;
  }, [tasks]);

  const agents = useMemo(() => {
    const names = [...new Set(tasks.map(t => t.agentName).filter(Boolean))];
    // Add any TEAM_MEMBERS not yet in tasks
    TEAM_MEMBERS.forEach(n => { if (!names.includes(n)) names.push(n); });
    return names.map(name => ({ name }));
  }, [tasks]);

  const roster = useMemo(() => buildRoster(agents, tasksByAgent, {}, {}), [agents, tasksByAgent]);

  // Summary stats
  const summary = useMemo(() => {
    const today = new Date().toISOString().split('T')[0];
    const allOverdue = tasks.filter(t => t.status !== 'completed' && t.dueDate < today).length;
    const allDueToday = tasks.filter(t => t.status !== 'completed' && t.dueDate === today).length;
    const allCompleted = tasks.filter(t => t.status === 'completed').length;
    const avgScore = roster.filter(r => r.score != null).reduce((s, r) => s + r.score, 0) /
      Math.max(1, roster.filter(r => r.score != null).length);
    return { overdue: allOverdue, dueToday: allDueToday, completed: allCompleted, total: tasks.length, avgScore: Math.round(avgScore) };
  }, [tasks, roster]);

  // Sort & filter
  const sortedRoster = useMemo(() => {
    let filtered = roster;
    if (searchQ.trim()) {
      const q = searchQ.toLowerCase();
      filtered = roster.filter(a => (a.name || '').toLowerCase().includes(q));
    }

    const dir = sortDir === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => {
      switch (sortCol) {
        case 'name': return dir * (a.name || '').localeCompare(b.name || '');
        case 'score': return dir * ((a.score || 0) - (b.score || 0));
        case 'overdue': return dir * ((a.metrics.overdue || 0) - (b.metrics.overdue || 0));
        case 'pipeline': return dir * ((DEMO_PIPELINE[a.name]?.value || 0) - (DEMO_PIPELINE[b.name]?.value || 0));
        case 'speed': return dir * ((DEMO_PIPELINE[a.name]?.speedToLead || 999) - (DEMO_PIPELINE[b.name]?.speedToLead || 999));
        case 'activity': return dir * ((a.metrics.daysSinceActivity || 0) - (b.metrics.daysSinceActivity || 0));
        default: return 0;
      }
    });
  }, [roster, sortCol, sortDir, searchQ]);

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('desc'); }
  };

  const sortArrow = (col) => {
    if (sortCol !== col) return null;
    return sortDir === 'asc' ? <ChevronUp size={10} /> : <ChevronDown size={10} />;
  };

  const thStyle = {
    padding: '10px 8px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase',
    color: theme.colors.textLight, letterSpacing: '0.5px', cursor: 'pointer',
    whiteSpace: 'nowrap', textAlign: 'center', userSelect: 'none',
    borderBottom: `2px solid ${theme.colors.border}`,
    background: theme.colors.bg,
  };

  // For agent role: find their own row and rank
  const myAgent = !isBroker && userName
    ? roster.find(a => a.name.toLowerCase() === userName.toLowerCase())
    : null;
  const myRank = !isBroker && myAgent
    ? [...roster].filter(a => a.score != null).sort((a, b) => (b.score || 0) - (a.score || 0)).findIndex(a => a.name === myAgent.name) + 1
    : null;

  return (
    <div style={{ padding: '16px 20px', maxWidth: 1300, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: theme.colors.text, margin: 0 }}>
            {isBroker ? 'Agent Activity' : 'My Activity'}
          </h1>
          {isBroker ? (
            <p style={{ fontSize: 12, color: theme.colors.textLight, margin: '4px 0 0', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <span>{roster.length} agent{roster.length !== 1 ? 's' : ''}</span>
              <span style={{ color: theme.colors.border }}>•</span>
              <span>Avg score: <strong style={{ color: theme.colors.text }}>{summary.avgScore}</strong></span>
              <span style={{ color: theme.colors.border }}>•</span>
              <span style={{ color: summary.overdue > 0 ? theme.colors.danger : theme.colors.success }}>
                {summary.overdue} overdue
              </span>
              <span style={{ color: theme.colors.border }}>•</span>
              <span style={{ color: theme.colors.warning }}>{summary.dueToday} due today</span>
              <span style={{ color: theme.colors.border }}>•</span>
              <span style={{ color: theme.colors.success }}>
                Pipeline: {formatCurrency(Object.values(DEMO_PIPELINE).reduce((s, p) => s + (p.value || 0), 0))}
              </span>
            </p>
          ) : myAgent && (
            <p style={{ fontSize: 12, color: theme.colors.textLight, margin: '4px 0 0' }}>
              Score: <strong style={{ color: gradeColor(myAgent.grade, theme.colors) }}>{myAgent.grade} ({myAgent.score})</strong>
              {myRank && <span> • Rank: <strong style={{ color: theme.colors.primary }}>#{myRank}</strong> of {roster.filter(a => a.score != null).length}</span>}
            </p>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* View toggle */}
          <div data-tour="activity-segments" style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: `1px solid ${theme.colors.border}` }}>
            {[
              { id: 'table', label: 'Table', Icon: LayoutList },
              { id: 'leaderboard', label: 'Leaderboard', Icon: Trophy },
            ].map(v => (
              <button key={v.id} onClick={() => setViewMode(v.id)} style={{
                display: 'flex', alignItems: 'center', gap: 4,
                padding: '6px 12px', fontSize: 11, fontWeight: 600, border: 'none', cursor: 'pointer',
                background: viewMode === v.id ? theme.colors.primary : theme.colors.card,
                color: viewMode === v.id ? 'white' : theme.colors.text,
              }}>
                <v.Icon size={12} /> {v.label}
              </button>
            ))}
          </div>
          {/* Search (broker only in table mode) */}
          {isBroker && viewMode === 'table' && (
            <div style={{ position: 'relative' }}>
              <Search size={13} style={{ position: 'absolute', left: 9, top: 9, color: theme.colors.textLight }} />
              <input
                placeholder="Search agents..."
                value={searchQ}
                onChange={e => setSearchQ(e.target.value)}
                style={{
                  padding: '7px 10px 7px 28px', fontSize: 12, borderRadius: 8,
                  border: `1px solid ${theme.colors.border}`, outline: 'none',
                  background: theme.colors.card, width: 180,
                }}
              />
            </div>
          )}
        </div>
      </div>

      {/* ═══ LEADERBOARD VIEW ═══ */}
      {viewMode === 'leaderboard' && (
        <LeaderboardView roster={roster} tasksByAgent={tasksByAgent} currentAgentName={isBroker ? null : userName} />
      )}

      {/* ═══ TABLE VIEW ═══ */}
      {viewMode === 'table' && (
        <>
          {/* Agent's own row summary (agent role only) */}
          {!isBroker && myAgent && (
            <div style={{
              background: theme.colors.card, borderRadius: 12,
              border: `1px solid ${theme.colors.border}`, overflow: 'hidden', marginBottom: 16,
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thStyle, textAlign: 'left', paddingLeft: 12, minWidth: 180 }}>Agent</th>
                      <th style={{ ...thStyle, minWidth: 80 }}>Score</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>Overdue</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>Due Today</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>On Track</th>
                      <th style={{ ...thStyle, minWidth: 70 }}>Active Leads</th>
                      <th style={{ ...thStyle, minWidth: 80 }}>Speed to Lead</th>
                      <th style={{ ...thStyle, minWidth: 70 }}>Follow-ups<br/>(7d)</th>
                      <th style={{ ...thStyle, minWidth: 80 }}>Pipeline $</th>
                      <th style={{ ...thStyle, minWidth: 70 }}>Last Activity</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>Trend</th>
                    </tr>
                  </thead>
                  <tbody>
                    <AgentRow
                      agent={myAgent}
                      tasks={tasksByAgent[myAgent.name] || []}
                      pipeline={DEMO_PIPELINE[myAgent.name]}
                      expanded={expandedAgent === myAgent.name}
                      onToggle={() => setExpandedAgent(expandedAgent === myAgent.name ? null : myAgent.name)}
                      onPing={() => {}} // agents don't ping themselves
                      hidePing
                    />
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Full team table (broker only) */}
          {isBroker && (
            <div data-tour="activity-roster" style={{
              background: theme.colors.card, borderRadius: 12,
              border: `1px solid ${theme.colors.border}`, overflow: 'hidden',
            }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th onClick={() => handleSort('name')}   style={{ ...thStyle, textAlign: 'left', paddingLeft: 12, minWidth: 180 }}>Agent {sortArrow('name')}</th>
                      <th onClick={() => handleSort('score')}  style={{ ...thStyle, minWidth: 80 }}>Score {sortArrow('score')}</th>
                      <th onClick={() => handleSort('overdue')} style={{ ...thStyle, minWidth: 60 }}>Overdue {sortArrow('overdue')}</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>Due Today</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>On Track</th>
                      <th style={{ ...thStyle, minWidth: 70 }}>Active Leads</th>
                      <th onClick={() => handleSort('speed')}  style={{ ...thStyle, minWidth: 80 }}>Speed to Lead {sortArrow('speed')}</th>
                      <th style={{ ...thStyle, minWidth: 70 }}>Follow-ups<br/>(7d)</th>
                      <th onClick={() => handleSort('pipeline')} style={{ ...thStyle, minWidth: 80 }}>Pipeline $ {sortArrow('pipeline')}</th>
                      <th onClick={() => handleSort('activity')} style={{ ...thStyle, minWidth: 70 }}>Last Activity {sortArrow('activity')}</th>
                      <th style={{ ...thStyle, minWidth: 60 }}>Trend</th>
                      <th style={{ ...thStyle, minWidth: 80 }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedRoster.map(agent => (
                      <AgentRow
                        key={agent.name}
                        agent={agent}
                        tasks={tasksByAgent[agent.name] || []}
                        pipeline={DEMO_PIPELINE[agent.name]}
                        expanded={expandedAgent === agent.name}
                        onToggle={() => setExpandedAgent(expandedAgent === agent.name ? null : agent.name)}
                        onPing={() => setPingAgent(agent)}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {sortedRoster.length === 0 && (
                <div style={{ padding: 40 }}>
                  {searchQ ? (
                    <EmptyState type="search" />
                  ) : (
                    <EmptyState
                      icon={<Target size={30} color={theme.colors.primary} />}
                      title="No activity data yet"
                      description="Agent scores and activity tracking will appear as your team starts working."
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Smart Ping Modal */}
      <SmartPingModal agent={pingAgent} onClose={() => setPingAgent(null)} showToast={showToast} />
    </div>
  );
};

export default AgentActivityView;
